import { useEffect, useMemo } from 'react'
import fetchMerchandiseList from 'actions/merchandise/fetchMerchandiseList'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getObjectKey from 'lib/object/getObjectKey'

interface Options {
  disabled?: boolean
}

/**
 * Fetches a merchandise list per `categoryId` (each scoped by `baseFilters` + that single category)
 * and returns a `{ [categoryId]: productCount }` map. Useful for deciding which categories qualify
 * for a UI surface (e.g. filter pills) before rendering them.
 *
 * Counts are read directly from the Redux cache, so a follow-up fetch with the same filter shape
 * (e.g. when a pill is selected) hits the cache instead of re-fetching.
 *
 * Returns `undefined` for ids whose count hasn't loaded yet — callers can wait on
 * `ids.every(id => counts[id] !== undefined)` before acting on the data.
 */
function useMerchandiseCategoryProductCounts(
  categoryIds: Array<string>,
  baseFilters: App.MerchandiseFilters | undefined,
  options: Options = {},
): Record<string, number | undefined> {
  const dispatch = useAppDispatch()

  const filtersByCategory = useMemo(() => {
    const map: Record<string, App.MerchandiseFilters> = {}
    if (!baseFilters) {
      return map
    }
    for (const id of categoryIds) {
      map[id] = { ...baseFilters, categories: [id] }
    }
    return map
  }, [categoryIds, baseFilters])

  useEffect(() => {
    if (options.disabled) {
      return
    }
    for (const filters of Object.values(filtersByCategory)) {
      dispatch(fetchMerchandiseList(filters))
    }
  }, [dispatch, options.disabled, filtersByCategory])

  return useAppSelector((state) => {
    const counts: Record<string, number | undefined> = {}
    for (const id of Object.keys(filtersByCategory)) {
      const key = getObjectKey(filtersByCategory[id])
      counts[id] = state.merchandise.merchandiseLists[key]?.length
    }
    return counts
  }, shallowEqual)
}

export default useMerchandiseCategoryProductCounts
